/**
 * Theme Engine
 * Applies theme colors to CSS variables in real-time.
 * Converts hex colors to HSL for Tailwind CSS variable format.
 */

export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
}

export interface ThemeConfig {
  colors: ThemeColors;
  mode: 'dark' | 'light';
  font: string;
}

const STORAGE_KEY = 'nexopdv_theme';

/**
 * Convert hex color to HSL string (without "hsl()" wrapper).
 * Returns format: "252 75% 64%" for Tailwind CSS variables.
 */
function hexToHSL(hex: string): string {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Darken a hex color by a percentage.
 */
function darken(hex: string, percent: number): string {
  hex = hex.replace('#', '');
  const r = Math.max(0, Math.round(parseInt(hex.substring(0, 2), 16) * (1 - percent / 100)));
  const g = Math.max(0, Math.round(parseInt(hex.substring(2, 4), 16) * (1 - percent / 100)));
  const b = Math.max(0, Math.round(parseInt(hex.substring(4, 6), 16) * (1 - percent / 100)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Lighten a hex color by a percentage.
 */
function lighten(hex: string, percent: number): string {
  hex = hex.replace('#', '');
  const r = Math.min(255, Math.round(parseInt(hex.substring(0, 2), 16) + (255 - parseInt(hex.substring(0, 2), 16)) * percent / 100));
  const g = Math.min(255, Math.round(parseInt(hex.substring(2, 4), 16) + (255 - parseInt(hex.substring(2, 4), 16)) * percent / 100));
  const b = Math.min(255, Math.round(parseInt(hex.substring(4, 6), 16) + (255 - parseInt(hex.substring(4, 6), 16)) * percent / 100));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Apply theme to the document by setting CSS variables.
 */
export function applyTheme(config: ThemeConfig): void {
  const root = document.documentElement;
  const { colors, mode } = config;

  // Primary
  root.style.setProperty('--primary', hexToHSL(colors.primary));
  root.style.setProperty('--ring', hexToHSL(colors.primary));

  // Secondary
  root.style.setProperty('--secondary', hexToHSL(colors.secondary));

  // Success/accent
  root.style.setProperty('--success', hexToHSL(colors.accent));

  if (mode === 'dark') {
    root.style.setProperty('--background', hexToHSL(colors.background));
    root.style.setProperty('--foreground', '0 0% 100%');
    root.style.setProperty('--card', hexToHSL(lighten(colors.background, 8)));
    root.style.setProperty('--card-foreground', '0 0% 100%');
    root.style.setProperty('--popover', hexToHSL(lighten(colors.background, 8)));
    root.style.setProperty('--popover-foreground', '0 0% 100%');
    root.style.setProperty('--muted', hexToHSL(lighten(colors.background, 12)));
    root.style.setProperty('--muted-foreground', '240 7% 66%');
    root.style.setProperty('--accent', hexToHSL(lighten(colors.background, 12)));
    root.style.setProperty('--accent-foreground', '0 0% 100%');
    root.style.setProperty('--border', hexToHSL(lighten(colors.background, 18)));
    root.style.setProperty('--input', hexToHSL(lighten(colors.background, 18)));
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.style.setProperty('--background', hexToHSL(colors.background));
    root.style.setProperty('--foreground', '240 33% 14%');
    root.style.setProperty('--card', '0 0% 100%');
    root.style.setProperty('--card-foreground', '240 33% 14%');
    root.style.setProperty('--popover', '0 0% 100%');
    root.style.setProperty('--popover-foreground', '240 33% 14%');
    root.style.setProperty('--muted', '220 20% 96%');
    root.style.setProperty('--muted-foreground', '215 16% 47%');
    root.style.setProperty('--accent', '220 20% 96%');
    root.style.setProperty('--accent-foreground', '240 33% 14%');
    root.style.setProperty('--border', '214 32% 91%');
    root.style.setProperty('--input', '214 32% 91%');
    root.classList.remove('dark');
    root.classList.add('light');
  }

  // Font
  if (config.font) {
    root.style.setProperty('font-family', `"${config.font}", system-ui, sans-serif`);
  }

  // Save to localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

/**
 * Load saved theme from localStorage and apply it.
 */
export function loadSavedTheme(): void {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const config: ThemeConfig = JSON.parse(saved);
      applyTheme(config);
    }
  } catch {
    // Ignore errors, use default theme
  }
}

/**
 * Reset theme to defaults.
 */
export function resetTheme(): void {
  localStorage.removeItem(STORAGE_KEY);
  // Remove inline styles
  const root = document.documentElement;
  root.style.removeProperty('--primary');
  root.style.removeProperty('--secondary');
  root.style.removeProperty('--success');
  root.style.removeProperty('--ring');
  root.style.removeProperty('--background');
  root.style.removeProperty('--foreground');
  root.style.removeProperty('--card');
  root.style.removeProperty('--card-foreground');
  root.style.removeProperty('--popover');
  root.style.removeProperty('--popover-foreground');
  root.style.removeProperty('--muted');
  root.style.removeProperty('--muted-foreground');
  root.style.removeProperty('--accent');
  root.style.removeProperty('--accent-foreground');
  root.style.removeProperty('--border');
  root.style.removeProperty('--input');
  root.style.removeProperty('font-family');
  root.classList.add('dark');
  root.classList.remove('light');
}
